import { useMemo } from 'react';
import { useUser } from '../Context/UserContext';
import grandfatheredConfig from '../config/grandfathered-features.json';

/**
 * Hook for checking grandfathered feature access
 * Grants access if the user's email domain is in the allowed list
 */
export const useGrandfatheredAccess = () => {
  const { userData } = useUser();

  return useMemo(() => {
    const userEmail = userData?.email;

    /**
     * Extract domain from email
     * @param {string} email
     * @returns {string|null}
     */
    const getEmailDomain = (email) => {
      if (!email || typeof email !== 'string') return null;
      const parts = email.split('@');
      return parts.length === 2 ? parts[1].toLowerCase() : null;
    };

    /**
     * Check if user has access to a specific grandfathered feature
     * @param {string} feature - 'seoBV' or 'leadBV'
     * @returns {boolean}
     */
    const hasGrandfatheredAccess = (feature) => {
      const featureConfig = grandfatheredConfig[feature];

      if (!featureConfig) {
        return false; // Feature not configured, deny access
      }

      const userDomain = getEmailDomain(userEmail);
      const normalizedEmail = userEmail?.toLowerCase();

      // Check if user's full email is in the allowed list
      if (normalizedEmail && featureConfig.allowedEmails?.some(
        email => email.toLowerCase() === normalizedEmail
      )) {
        return true;
      }

      // Check if user's email domain is in the allowed list
      if (userDomain && featureConfig.allowedEmailDomains?.some(
        domain => domain.toLowerCase() === userDomain
      )) {
        return true;
      }

      return false;
    };

    return {
      hasSeoBVAccess: hasGrandfatheredAccess('seoBV'),
      hasLeadBVAccess: hasGrandfatheredAccess('leadBV'),
      hasGrandfatheredAccess, // Expose for custom feature checks
      isLoading: !userData // True if context data not yet loaded
    };
  }, [userData]);
};

export default useGrandfatheredAccess;
